import {
	getSolutionDemoCriticalPreloadImageSrcs,
	getSolutionDemoDeferredPreloadImageSrcs,
} from "./solution-demo-preload-assets";

export type SolutionDemoRuntimePreloadSegment =
	| "topicViewer"
	| "completionOverlay";

const topicViewerModuleId = "@saas/learn/components/TopicLearnerSwiper";
const completionOverlayModuleId =
	"@saas/learn/components/completion-overlay/completion-overlay-view";
const completionBackdropModuleId =
	"@saas/learn/components/completion-overlay/completion-backdrop";
const grainientModuleId = "@repo/ui/components/react-bits/grainient";

let topicViewerPreloadPromise: Promise<void> | null = null;
let completionOverlayPreloadPromise: Promise<void> | null = null;
let criticalVisualAssetsPreloadPromise: Promise<void> | null = null;
let deferredVisualAssetsPreloadPromise: Promise<void> | null = null;

function preloadImageSrc(src: string): Promise<void> {
	if (typeof window === "undefined") {
		return Promise.resolve();
	}

	return new Promise((resolve) => {
		const image = new Image();
		const finish = () => {
			resolve();
		};
		image.onload = finish;
		image.onerror = finish;
		image.src = src;
	});
}

function preloadImageSrcList(srcs: readonly string[]): Promise<void> {
	if (srcs.length === 0) {
		return Promise.resolve();
	}
	return Promise.all(srcs.map(preloadImageSrc)).then(() => undefined);
}

function preloadCriticalSolutionDemoVisualAssets(): Promise<void> {
	if (!criticalVisualAssetsPreloadPromise) {
		criticalVisualAssetsPreloadPromise = preloadImageSrcList(
			getSolutionDemoCriticalPreloadImageSrcs(),
		);
	}
	return criticalVisualAssetsPreloadPromise;
}

function preloadDeferredSolutionDemoVisualAssets(): Promise<void> {
	if (!deferredVisualAssetsPreloadPromise) {
		deferredVisualAssetsPreloadPromise =
			preloadCriticalSolutionDemoVisualAssets()
				.then(() =>
					preloadImageSrcList(
						getSolutionDemoDeferredPreloadImageSrcs(),
					),
				)
				.then(() => undefined);
	}
	return deferredVisualAssetsPreloadPromise;
}

function scheduleDeferredSolutionDemoVisualAssets(): void {
	void preloadDeferredSolutionDemoVisualAssets();
}

export function preloadSolutionDemoRuntimeSegment(
	segment: SolutionDemoRuntimePreloadSegment,
): Promise<void> {
	if (segment === "topicViewer") {
		if (!topicViewerPreloadPromise) {
			topicViewerPreloadPromise = Promise.all([
				import(topicViewerModuleId),
				import(grainientModuleId),
				preloadCriticalSolutionDemoVisualAssets(),
			]).then(() => {
				scheduleDeferredSolutionDemoVisualAssets();
				return undefined;
			});
		}
		return topicViewerPreloadPromise;
	}

	if (!completionOverlayPreloadPromise) {
		completionOverlayPreloadPromise = Promise.all([
			import(completionOverlayModuleId),
			import(completionBackdropModuleId),
			import(grainientModuleId),
			import("canvas-confetti"),
			preloadCriticalSolutionDemoVisualAssets(),
		]).then(() => {
			scheduleDeferredSolutionDemoVisualAssets();
			return undefined;
		});
	}
	return completionOverlayPreloadPromise;
}

export function preloadSolutionDemoRuntime(): Promise<void> {
	return Promise.all([
		preloadSolutionDemoRuntimeSegment("topicViewer"),
		preloadSolutionDemoRuntimeSegment("completionOverlay"),
	]).then(() => undefined);
}
